import { getApiResponseMessage, getRequestEndpoint } from './apiErrorUtils';

// These endpoints establish a session; a rejection does not mean an existing session expired.
export const isSignInRequest = (error) =>
  /^\/api\/(?:auth\/exchange-password|employee\/(?:login|microsoft-login))\/?$/i.test(
    getRequestEndpoint(error)
  );

export const isInactiveTenantError = (error) => {
  const message = (
    getApiResponseMessage(error?.response?.data) ||
    error?.message ||
    ''
  ).toLowerCase();

  const identifiesTenant = /\b(tenant|organisation|organization|company|workspace)\b/i.test(message);
  const identifiesInactiveState =
    /\b(inactive|deactivat(?:ed|ion)|disabled|suspended|deleted|not active)\b/i.test(message);

  return identifiesTenant && identifiesInactiveState;
};

export const buildPasswordLoginFeedback = (error) => {
  const status = error?.response?.status;
  const message = getApiResponseMessage(error?.response?.data);
  const feedback = { title: 'Unable to sign in', type: 'error' };

  if (isInactiveTenantError(error)) {
    return {
      ...feedback,
      title: 'Organization unavailable',
      message: 'This organization is inactive. Please contact your administrator or ShiftSmart support to restore access.',
    };
  }

  // The API also uses 401 for lockouts, so inspect its reason before the status code.
  if (/\b(lock(?:ed|out)|temporarily blocked)\b/i.test(message)) {
    const wait = message.match(/\b(\d+)\s*(minutes?|mins?|seconds?|secs?|hours?|hrs?)\b/i);
    let retryMessage = 'Please wait before trying again. If you need help, contact support.';
    if (wait) {
      const amount = Number(wait[1]);
      const unit = /^m/i.test(wait[2]) ? 'minute' : /^s/i.test(wait[2]) ? 'second' : 'hour';
      retryMessage = `Please try again in ${amount} ${unit}${amount === 1 ? '' : 's'}.`;
    }
    return {
      title: 'Account temporarily locked',
      type: 'warning',
      message: `Your account is temporarily locked. ${retryMessage}`,
    };
  }

  if (status === 429) {
    return {
      title: 'Too many sign-in attempts',
      type: 'warning',
      message: message || 'Please wait a few minutes before trying again.',
    };
  }

  if (status === 401) {
    return {
      ...feedback,
      title: 'Invalid email or password',
      message: 'Please try again, or use Forgot Password to reset your password.',
    };
  }

  if (status === 403) {
    return {
      ...feedback,
      title: 'Account access denied',
      message: message || 'Your account does not have access. Please contact your administrator or support.',
    };
  }

  if (error?.type === 'network' || (!error?.response && error?.request)) {
    return {
      ...feedback,
      title: 'Connection problem',
      message: 'We could not connect to the sign-in service. Check your internet connection and try again.',
    };
  }

  if (status >= 500) {
    return {
      ...feedback,
      title: 'Sign-in service unavailable',
      message: 'We could not sign you in because the service is temporarily unavailable. Please try again later.',
    };
  }

  return {
    ...feedback,
    message: message || 'We could not sign you in. Please try again. If the problem continues, contact support.',
  };
};
